/** Subcomponents for AvatarPickerModal — avatar card + small UI chips. */
import { useTranslation } from "react-i18next";
import { Loader2, Pencil, Pin, Sparkles, Trash2 } from "lucide-react";
import type { UgcAvatar } from "./api";

export function FilterButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg transition ${
        active ? "bg-white/[0.08] text-white" : "text-white/60 hover:bg-white/[0.04]"
      }`}
    >
      {label}
    </button>
  );
}

export function GenderChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 rounded-md text-xs transition ${
        active
          ? "bg-white/[0.1] text-white border border-white/15"
          : "bg-white/[0.03] text-white/60 border border-transparent"
      }`}
    >
      {label}
    </button>
  );
}

interface AvatarCardProps {
  avatar: UgcAvatar;
  selected: boolean;
  onSelect: () => void;
  onPin: () => void;
  onEdit: () => void;
  onRemove: () => void;
  onRecreateSimilar: () => void;
  recreatePending?: boolean;
}

export function AvatarCard({
  avatar,
  selected,
  onSelect,
  onPin,
  onEdit,
  onRemove,
  onRecreateSimilar,
  recreatePending,
}: AvatarCardProps) {
  const { t } = useTranslation();
  return (
    <div
      className={`group relative rounded-xl overflow-hidden border cursor-pointer transition ${
        selected
          ? "border-lime-400 ring-2 ring-lime-400/30"
          : "border-white/[0.08] hover:border-white/20"
      }`}
      onClick={onSelect}
    >
      <div className="aspect-[3/4] bg-white/[0.03]">
        <img src={avatar.signedUrl} alt={avatar.name} className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
        <div className="text-white text-xs font-medium truncate">{avatar.name}</div>
      </div>
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPin();
          }}
          className={`p-1.5 rounded-md backdrop-blur ${avatar.isPinned ? "bg-lime-400 text-black" : "bg-black/60 text-white/80"}`}
          aria-label={t("ugcStudio.avatar.pinLabel")}
        >
          <Pin className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRecreateSimilar();
          }}
          disabled={recreatePending}
          className="p-1.5 rounded-md bg-black/60 text-violet-300 hover:text-violet-200 disabled:opacity-50"
          aria-label={t("ugcStudio.avatar.generateSimilar")}
          title={t("ugcStudio.avatar.generateSimilarTitle")}
        >
          {recreatePending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Sparkles className="w-3 h-3" />
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="p-1.5 rounded-md bg-black/60 text-white/80 hover:text-white"
          aria-label={t("ugcStudio.avatar.editLabel")}
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1.5 rounded-md bg-black/60 text-red-300"
          aria-label={t("ugcStudio.avatar.deleteLabel")}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
